/**
 * Format properties to move fieldConfig options to the main property object
 * This makes the property structure cleaner and easier to work with in the UI
 * 
 * @param {Object|Array} properties - The properties object from Redux store or array of properties
 * @param {string} objectType - The type of object (contacts, deals, etc.)
 * @returns {Array} - Formatted properties array
 */
export const formatProperties = (properties, objectType) => {
  // Handle case where properties is already an array (from BulkEditModal)
  let propsArray = [];
  if (Array.isArray(properties)) {
    propsArray = properties;
  } else if (properties) {
    // Original behavior - extract properties from objectType object
    propsArray = Object.entries(properties).flatMap(([group, props]) => 
      Array.isArray(props) ? props.map(prop => ({ ...prop, group })) : []
    );
  }
  
  const formattedProperties = propsArray.map(prop => {
    // Create a new property object to avoid mutating the original
    const formattedProp = { ...prop };

    // Move options from fieldConfig to main property object
    if (prop['fieldConfig']?.['options']) formattedProp['options'] = prop['fieldConfig']['options'];

    // Add any other fieldConfig properties that might be useful
    if (prop['fieldConfig']?.['min'] !== undefined) formattedProp['min'] = prop['fieldConfig']['min'];
    if (prop['fieldConfig']?.['max'] !== undefined) formattedProp['max'] = prop['fieldConfig']['max'];

    return formattedProp;
  });

  // Sort properties by order
  formattedProperties.sort((a, b) => {
    // If both have order values, sort by order
    if (a.order !== undefined && b.order !== undefined) {
      return a.order - b.order;
    }
    // If only one has order, it comes first
    if (a.order !== undefined) return -1;
    if (b.order !== undefined) return 1;
    // If neither has order, sort alphabetically by label
    return (a.label || '').localeCompare(b.label || '');
  });

  return formattedProperties;
};

/**
 * Format a single property
 * @param {Object} property - Single property object
 * @returns {Object} - Formatted property
 */
export const formatSingleProperty = (property) => {
  const formattedProp = { ...property };

  // Move options from fieldConfig to main property object
  if (property['fieldConfig']?.['options']) formattedProp['options'] = property['fieldConfig']['options'];

  // Add any other fieldConfig properties
  if (property['fieldConfig']?.['min'] !== undefined) formattedProp['min'] = property['fieldConfig']['min'];
  if (property['fieldConfig']?.['max'] !== undefined) formattedProp['max'] = property['fieldConfig']['max'];

  return formattedProp;
};

/**
 * Format properties for a specific objectType/objectType
 * @param {Object} state - Redux state containing properties
 * @param {string} objectType - Module name (contacts, deals, etc.)
 * @returns {Array} - Formatted properties array
 */
export const getFormattedPropertiesForModule = (state, objectType) => {
  const properties = state['Contact']?.['properties'] || {};
  return formatProperties(properties, objectType);
};

/**
 * Group formatted properties by their group name
 * @param {Array} properties - Array of formatted properties
 * @returns {Object} - Properties grouped by group name
 */
export const groupFormattedProperties = (properties) => {
  return properties.reduce((acc, prop) => {
    const groupName = prop['group'] || 'Custom Properties';
    if (!acc[groupName]) acc[groupName] = [];
    acc[groupName].push(prop);
    return acc;
  }, {});
};

/**
 * Get property value from data object (handles both root level and customFields)
 * @param {Object} data - The data object (contact, deal, etc.)
 * @param {Object} property - The property definition
 * @returns {*} - The property value
 */
export const getPropertyValue = (data, property) => {
  if (!data || !property) return null;

  // For custom fields (isSystem: false), look in customFields
  if (property.isSystem === false && data.customFields) return data.customFields[property.name];

  // For system fields with nested paths (e.g., 'location.city'), traverse the object
  if (property.name && property.name.includes('.')) {
    const pathParts = property.name.split('.');
    let value = data;
    for (const part of pathParts) {
      if (value && typeof value === 'object') value = value[part];
      else return null;
    }
    return value;
  }
  // For system fields, look at root level
  return data[property.name];
};

/**
 * Format property value based on property type
 * @param {*} value - The raw value
 * @param {Object} property - The property definition
 * @param {Object} options - Additional options (e.g., currentWorkspace for user formatting)
 * @returns {string} - Formatted value for display
 */
export const formatPropertyValue = (value, property, options = {}) => {
  if (!value && value !== 0 && value !== false) return '-';

  switch (property?.type) {
    case 'currency':
      return `$${Number(value).toFixed(2)}`;
    
    case 'percentage':
      return `${value}%`;
    
    case 'date':
      return new Date(value).toLocaleDateString();
    
    case 'datetime':
      return new Date(value).toLocaleString();
    
    case 'boolean':
      return value ? 'Yes' : 'No';
    
    case 'select':
      const option = property.options?.find(opt => opt.value === value);
      // Stale workspace property docs can miss newer options (e.g. 'valid' had no label) — prettify the raw value instead of showing it lowercase
      return option?.label || String(value).split(/[-_\s]+/).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    
    case 'multiselect':
      if (Array.isArray(value)) {
        return value.map(v => {
          // Reason: tags are stored as { name, color } objects — show the name, not "[object Object]"
          const raw = v && typeof v === 'object' ? v['name'] : v;
          const opt = property.options?.find(o => o.value === raw);
          return opt?.label || raw;
        }).filter(Boolean).join(', ');
      }
      return value;
    
    case 'user':
      if (typeof value === 'object' && value !== null) {
        if (value.firstName || value.lastName) {
          return `${value.firstName || ''} ${value.lastName || ''}`.trim();
        }
        if (value.email) return value.email;
        if (value.name) return value.name;
        return 'User';
      }
      // If it's a string (like user ID), try to find the user in workspace members
      if (value && options.currentWorkspace?.members) {
        const user = options.currentWorkspace.members.find(u => u._id === value);
        if (user) {
          if (user.firstName || user.lastName) {
            return `${user.firstName || ''} ${user.lastName || ''}`.trim();
          }
          return user.email || value;
        }
      }
      return value || '-';
    
    case 'reference':
    case 'contact':
    case 'company':
      if (typeof value === 'object' && value !== null) {
        if (value.name) return value.name;
        if (value.title) return value.title;
        if (value.firstName && value.lastName) {
          return `${value.firstName} ${value.lastName}`;
        }
        if (value._id) return `ID: ${value._id}`;
        return '[Object]';
      }
      return value || '-';
    
    case 'number':
      return Number(value).toString();
    
    case 'email':
    case 'phone':
    case 'url':
    case 'text':
    case 'textarea':
    default:
      // Make sure we never return an object directly
      if (typeof value === 'object' && value !== null) {
        if (value.toString && value.toString() !== '[object Object]') {
          return value.toString();
        }
        return JSON.stringify(value);
      }
      return value?.toString() || '-';
  }
};

/**
 * Check if a property is a custom field
 * @param {Object} property - The property definition
 * @returns {boolean} - True if it's a custom field
 */
export const isCustomField = (property) => {
  return property?.isSystem === false;
};

/**
 * Get only custom fields from properties array
 * @param {Array} properties - Array of all properties
 * @returns {Array} - Array of custom field properties
 */
export const getCustomFields = (properties) => {
  if (!Array.isArray(properties)) return [];
  return properties.filter(prop => isCustomField(prop));
};